import { useMutation, useReadQuery, type QueryRef } from '@apollo/client/react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router';

import { TaskCriticalityEnum } from 'src/graphql/schema';
import { MarkAsReadTasksDocument } from 'src/graphql/actions/tasks/markAsReadTasks.mutation';
import { type ITasksQuery, type ITasksQueryVariables } from 'src/graphql/actions/tasks/tasks.query';
import { ClientError, getApiError } from 'src/utils/errors';
import { pluralize } from 'src/utils/pluralize';
import { cn } from 'src/lib/utils';
import { CRITICALITY_CONFIG, CRITICALITY_ORDER, getWorstCriticality } from 'src/shared/mappings/criticality';
import { CriticalityIcon, eventTypeKeys, getTaskContent, setTaskUrl, useRefetchTasks } from 'src/containers/Tasks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { UNREAD_TASKS_LIMIT } from '../model';

import UnreadTaskRow, { type ITaskNode } from './UnreadTaskRow';

type Props = {
  queryRefs: Record<TaskCriticalityEnum, QueryRef<ITasksQuery, ITasksQueryVariables>>;
};

const UnreadTasksCard = ({ queryRefs }: Props) => {
  const { data: highData } = useReadQuery(queryRefs[TaskCriticalityEnum.High]);
  const { data: mediumData } = useReadQuery(queryRefs[TaskCriticalityEnum.Medium]);
  const { data: lowData } = useReadQuery(queryRefs[TaskCriticalityEnum.Low]);

  const navigate = useNavigate();
  const refetchTasks = useRefetchTasks();
  const [markAsRead] = useMutation(MarkAsReadTasksDocument);

  const tasksByCriticality = {
    [TaskCriticalityEnum.High]: highData.tasks,
    [TaskCriticalityEnum.Medium]: mediumData.tasks,
    [TaskCriticalityEnum.Low]: lowData.tasks,
  };

  const taskCounts = CRITICALITY_ORDER.map((criticality) => ({
    criticality,
    count: tasksByCriticality[criticality].nodesCount,
  }));
  const totalCount = taskCounts.reduce((sum, taskCount) => sum + taskCount.count, 0);

  if (!totalCount) return null;

  const worstCriticality = getWorstCriticality(taskCounts) ?? TaskCriticalityEnum.Low;
  const { bannerClassName } = CRITICALITY_CONFIG[worstCriticality];

  const tasks = CRITICALITY_ORDER.flatMap((criticality) => tasksByCriticality[criticality].nodes)
    .flatMap((task) => {
      const content = getTaskContent({
        eventType: task.eventType as (typeof eventTypeKeys)[number],
        payload: task.payload,
        parentType: task.parentType,
      });

      return content ? [{ task, content }] : [];
    })
    .slice(0, UNREAD_TASKS_LIMIT);

  const markTaskAsRead = async (taskId: string) => {
    try {
      const { data } = await markAsRead({ variables: { taskIds: [taskId] } });

      if (!data?.markAsReadTasks?.success) {
        throw new ClientError(data?.markAsReadTasks?.errors?.[0]);
      }

      refetchTasks();
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const handleTaskSelect = (task: ITaskNode) => {
    void markTaskAsRead(task.id);

    const { to, state } = setTaskUrl(task);
    navigate(to, { state });
  };

  return (
    <Card className={cn('rounded-xl shadow-none', bannerClassName)}>
      <CardHeader className="flex-row items-start gap-3 space-y-0 p-4">
        <CriticalityIcon criticality={worstCriticality} className="size-9.5" iconClassName="size-5.5" />
        <div className="grow space-y-1 pt-px self-center">
          <CardTitle>Unread tasks</CardTitle>
          <CardDescription className="text-xs text-foreground/75">
            Showing {tasks.length} of {pluralize(totalCount, 'task', 'tasks')}
          </CardDescription>
        </div>
        <Button variant="secondary" size="sm" asChild>
          <Link to="/tasks">View all</Link>
        </Button>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <ul className="space-y-1">
          {tasks.map(({ task, content }) => (
            <UnreadTaskRow key={task.id} task={task} content={content} onSelect={handleTaskSelect} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default UnreadTasksCard;
